import React, { useEffect, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../firebase";
import { Heading, Subheading } from "../../../../design/heading";
import { Switch, SwitchField } from "../../../../design/switch";
import { Description, Label } from "../../../../design/fieldset";
import { Button } from "../../../../design/button";
import { Text } from "../../../../design/text";
import { Divider } from "../../../../design/divider";
import { Badge } from "../../../../design/badge";
import { useAccount } from "../../../../contexts/AccountContext";
import { AccountService } from "../../../../services/AccountService";
import SettingsHeader from "../SettingsHeader";

const PaymentsSettings: React.FC = () => {
  const { account } = useAccount();
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  // While a save is in flight, don't let the effect below re-sync the toggle
  // from the (freshly re-created) account object and fight the user / clobber
  // a rollback.
  const isSaving = useRef(false);

  const chargesEnabled = Boolean((account as any)?.charges_enabled);
  const hasConnectAccount = Boolean(account?.stripeAccountId);

  useEffect(() => {
    if (isSaving.current) return;
    setPaymentsEnabled(account?.settings?.features?.enableStripePayments ?? false);
  }, [account]);

  const handleToggle = async (checked: boolean) => {
    if (!account?.id) return;
    isSaving.current = true;
    setToggleError(null);
    setPaymentsEnabled(checked);
    try {
      await AccountService.updateAccount(account.id, {
        settings: {
          ...account.settings,
          features: {
            ...account.settings?.features,
            enableStripePayments: checked,
          },
        },
      } as any);
    } catch (e) {
      console.error("Error updating payments setting:", e);
      setPaymentsEnabled(!checked);
      setToggleError(
        "Failed to update payment setting. Please try again.",
      );
    } finally {
      isSaving.current = false;
    }
  };

  const handleOnboarding = async () => {
    setIsRedirecting(true);
    try {
      const fetchStripeConnectOnboardingLink = httpsCallable(
        functions,
        "fetchStripeConnectOnboardingLink",
      );
      const { data } = (await fetchStripeConnectOnboardingLink({
        refreshUrl: window.location.origin + "/settings/payments",
        returnUrl: window.location.origin + "/settings/payments",
      })) as { data: { url: string } };
      window.open(data.url, "_blank");
    } catch (error) {
      console.error("Error starting Stripe onboarding:", error);
      alert("Failed to open Stripe onboarding. Please try again later.");
    }
    setIsRedirecting(false);
  };

  return (
    <>
      <SettingsHeader />

      <div className="space-y-8">
        <div>
          <Heading>Payments</Heading>
          <Text className="mt-2 text-zinc-600 dark:text-zinc-400">
            Collect visit payments online through Stripe — including before a
            visit takes place.
          </Text>
        </div>

        <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
        <div className="flex items-center justify-between">
          <Subheading>Stripe Account</Subheading>
          {chargesEnabled ? (
            <Badge color="green">Ready to accept payments</Badge>
          ) : hasConnectAccount ? (
            <Badge color="amber">Setup incomplete</Badge>
          ) : (
            <Badge color="zinc">Not set up</Badge>
          )}
        </div>
        <Text className="mt-2">
          Payments are processed on your own Stripe account — funds settle
          directly to your bank. Stripe requires some business details before
          you can accept charges.
        </Text>
        {!chargesEnabled && (
          <div className="mt-4">
            <Button onClick={handleOnboarding} disabled={isRedirecting}>
              {isRedirecting
                ? "Opening Stripe..."
                : hasConnectAccount
                  ? "Continue Stripe Setup"
                  : "Set Up Stripe"}
            </Button>
          </div>
        )}

        <Divider className="my-6" />

        <SwitchField>
          <Label>Enable online payments</Label>
          <Description>
            Adds a &quot;Request Payment&quot; option to visits so you can send
            parents a secure Stripe payment link — including before the visit
            takes place. Paid visits are marked automatically.
          </Description>
          <Switch
            checked={paymentsEnabled}
            onChange={handleToggle}
            disabled={!chargesEnabled}
          />
        </SwitchField>
          {!chargesEnabled && (
            <Text className="mt-2 text-sm">
              Finish Stripe setup above to enable online payments.
            </Text>
          )}
          {toggleError && (
            <Text className="mt-2 text-sm text-red-600 dark:text-red-400">
              {toggleError}
            </Text>
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentsSettings;
